import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** Layout for the public legal pages (Privacy / Terms). These are intentionally
 *  bilingual on a single page (English then Bulgarian) so a Google OAuth reviewer
 *  can read the English text directly while local users get Bulgarian. The legal
 *  body is static content, not app chrome — only the surrounding navigation goes
 *  through i18n. Rendered outside ProtectedRoute so the URLs are public. */
export function LegalShell({
  children,
  backTo = '/login',
  backLabel,
}: {
  children: ReactNode;
  /** Where the bottom "back" link points (default the sign-in page). */
  backTo?: string;
  /** Label for the back link (default "Back to sign in"). */
  backLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:py-16 text-foreground">
      <div className="space-y-4 text-sm leading-relaxed">{children}</div>
      <p className="mt-10 border-t border-border pt-6 text-sm">
        <Link to={backTo} className="text-primary hover:underline">
          ← {backLabel ?? t('legal.backToLogin')}
        </Link>
      </p>
    </div>
  );
}

export function LegalTitle({ children }: { children: ReactNode }) {
  return <h1 className="text-2xl font-semibold">{children}</h1>;
}

export function LegalUpdated({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

export function LegalH2({ children }: { children: ReactNode }) {
  return <h2 className="mt-8 text-base font-semibold">{children}</h2>;
}

/** Language separator between the English and Bulgarian halves. */
export function LegalDivider({ label }: { label: string }) {
  return (
    <div className="mt-12 flex items-center gap-3 pt-2 text-xs uppercase tracking-wide text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      {label}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
